import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { logger } from 'Services/logger/logger';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useUser from 'Utilities/hooks/useUser';

import { getTransportAndTransportOptions } from './getTransportAndTransportOptions';
import type { UseTransportAndTransportOptionsResponse } from './useTransportAndTransportOptions.types';

export const useTransportAndTransportOptions = (): UseQueryResult<UseTransportAndTransportOptionsResponse> & {
	shouldFetchTransportOptions: boolean;
} => {
	const { bidding, id } = useVehicleData();
	const { hasDealershipWonVehicleAndSellerConfirmed } = bidding;
	const user = useUser();

	const isEstimatedDeliveryCostsEnabled = useFeatureToggle(FEATURES.estimatedDeliveryCosts);
	const shouldFetchTransportOptions = Boolean(
		isEstimatedDeliveryCostsEnabled || hasDealershipWonVehicleAndSellerConfirmed,
	);

	return {
		...useQuery(
			['getTransportAndTransportOptions', id, hasDealershipWonVehicleAndSellerConfirmed, `${user.id}-${user.dealerId}`],
			() => getTransportAndTransportOptions({ id, isPostSaleAction: hasDealershipWonVehicleAndSellerConfirmed, user }),

			{
				enabled: shouldFetchTransportOptions,
				onError: (error) => {
					const err = error instanceof Error ? error : new Error('Error fetching transport and transport options');
					logger.warn({ error: err, message: err.message, scope: 'fetchTransportAndTransportOptions' });
				},
				refetchOnWindowFocus: false,
				retry: 2,
			},
		),
		shouldFetchTransportOptions,
	};
};
